/*
 * 0.68: Welches Datum zeigt der Kalender? Frist (Standard), Start oder ein eigenes Datumsfeld des Boards.
 * Die Wahl ist eine persönliche Einstellung je Board und Konto auf diesem Gerät (`accountPref`).
 */
import { calendarDate, type CardSummary, type CustomField } from '@kaba/shared';
import { accountPref, setAccountPref } from './boardNav';

export type CalendarSource = 'due' | 'start' | `field:${string}`;

export interface SourceOption {
  value: CalendarSource;
  /** Name im Auswahlfeld */
  label: string;
  /** „Ohne …“ in der Seitenleiste und leere Agenda */
  noun: string;
}

export function sourceOptions(fields: Pick<CustomField, 'id' | 'name' | 'type'>[]): SourceOption[] {
  return [
    { value: 'due', label: 'Frist', noun: 'Frist' },
    { value: 'start', label: 'Start', noun: 'Start' },
    ...fields.filter((f) => f.type === 'date').map((f) => ({ value: `field:${f.id}` as const, label: f.name, noun: `„${f.name}“` })),
  ];
}

/** Gespeicherte Wahl; fehlt das Feld inzwischen, gilt wieder die Frist */
export function resolveSource(value: string | null | undefined, fields: Pick<CustomField, 'id' | 'name' | 'type'>[]): SourceOption {
  const opts = sourceOptions(fields);
  return opts.find((o) => o.value === value) ?? opts[0]!;
}

/** Kalendertag (JJJJ-MM-TT) einer Karte für die Quelle, sonst null */
export function cardDay(card: Pick<CardSummary, 'dueAt' | 'dueHasTime' | 'startAt' | 'startHasTime' | 'custom'>, source: CalendarSource): string | null {
  if (source === 'due') return card.dueAt ? calendarDate(card.dueAt, card.dueHasTime) : null;
  if (source === 'start') return card.startAt ? calendarDate(card.startAt, card.startHasTime) : null;
  const v = card.custom?.[source.slice(6)];
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

/** Uhrzeit (ISO) der Karte für die Quelle, wenn es eine gibt – zum Sortieren und Anzeigen */
export function cardTime(card: Pick<CardSummary, 'dueAt' | 'dueHasTime' | 'startAt' | 'startHasTime'>, source: CalendarSource): string | null {
  if (source === 'due') return card.dueAt && card.dueHasTime ? card.dueAt : null;
  if (source === 'start') return card.startAt && card.startHasTime ? card.startAt : null;
  return null;
}

/** Tage zwischen zwei Kalenderdaten (b − a) */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by!, bm! - 1, bd!) - Date.UTC(ay!, am! - 1, ad!)) / 86_400_000);
}

export const storedSource = (boardId: string) => accountPref<string>(`calendarSource.${boardId}`, 'due');
export const storeSource = (boardId: string, v: CalendarSource) => setAccountPref(`calendarSource.${boardId}`, v);



/** Kalenderdatum + n Tage */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}
